import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateAdapterOptions } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import moment from 'moment';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import {
    DatesetEditorDateTimeUnits,
    DatesetEditorDateTimeUnitsWithDays,
    DateTimeUnitInfo,
    isCalendarUnit,
    relativeDateTimeUnitToMomentUnit
} from 'src/app/shared/compatibility/filter.models';
import {
    AdvancedDatasetCheckboxInputDto,
    AdvancedDatasetDropdownInputDto,
    AdvancedDatasetDto,
    AdvancedDatasetInputDto,
    AdvancedDatasetInputType,
    AdvancedDatasetNumberRangeInputDto,
    AdvancedDatasetRelativeDatesInputValueDto,
    AdvancedDatasetRelativeDatesOperator,
    CustomDatasetDropdownService,
    DropdownOptionDto,
    NumberRangeValue,
    RelativeDateOrTimeOperator,
    RelativeDateOrTimeValuesDto,
    RelativeDateTimeFilterUnit,
    YearPeriod
} from 'src/app/services/api.services';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { catchAppError, createSearchPattern, isNullOrUndefined } from 'src/app/shared/utils';
import { DateFormatType } from 'src/app/shared/utils/moment';
import { DatasetInput } from '../../models/dataset-editor.models';
import { DatasetEditorDialogComponent } from '../visual-editor/dialogs/dataset-editor-dialog.component';

const RelativeYearPeriodMomentFormats = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  }
};

@Component({
  selector: 'app-dataset-editor__input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
            useValue: <MatMomentDateAdapterOptions>{
                useUtc: true
            }
        },
        {
            provide: MAT_DATE_FORMATS,
            useFactory: (settings: AppSettingsService, c: DatasetEditorInputComponent) => {
                if (c.input.template.type == AdvancedDatasetInputType.RelativeYearPeriod
                    || c.input.template.type == AdvancedDatasetInputType.YearPeriod) {
                    return RelativeYearPeriodMomentFormats;
                }
                else {
                    return settings.momentFormats[DateFormatType.Date];
                }
            },
            deps: [AppSettingsService, DatasetEditorInputComponent]
        }
    ],
  standalone: false
})
export class DatasetEditorInputComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() locale = 'en-US';

  @Input() product = '';
  @Input() dataset: AdvancedDatasetDto;

  @Input() input: DatasetInput;

  @Input() searchPattern = '';

  @Output() inputChange = new EventEmitter<any>();

  @Input() spendBillingCenter: string = null;
  
  @Input() validateUIEmitter: EventEmitter<void>;

  checkboxValue: boolean;

  textValue: string;

  numberValue: number;

  startNumber: number;
  endNumber: number;

  startDate: any;
  endDate: any;

  maxDate: any;
  minDate: any;

  maximumDateRange: number;

  numberRangeValueMin: number = null;
  numberRangeValueMax: number = null;
  numberRangeValue1: number;
  numberRangeValue2: number;

  //#region Relative Dates
  relativeDates = false;
  relativeDatesOperator = AdvancedDatasetRelativeDatesOperator.InLast;
  relativeDatesValue: AdvancedDatasetRelativeDatesInputValueDto = { includeToday: true};

  relativeMaxUnits: number;
  relativeMaxUnits2: number;

  filterValuesSub = new BehaviorSubject<RelativeDateOrTimeValuesDto>({});

  private unsubscribe$ = new Subject<void>();

  toggleRelativeDates(value: boolean) {
    if (value == this.relativeDates)
      return;

    this.relativeDates = value;

    // Cosmetic only: show a unit in the dropdown instead of leaving it visually blank.
    // Does not change what gets saved/sent - the backend already handles a missing unit safely.
    if (value && isNullOrUndefined(this.relativeDatesValue.unitType)) {
      this.relativeDatesValue = {
        ...this.relativeDatesValue,
        unitType: RelativeDateTimeFilterUnit.CalendarMonths
      };
    }

    this.onValueChange();
  }

  relativeDateUnits = this.getRelativeDateUnits();

  getRelativeDateUnits() {
    let units = DatesetEditorDateTimeUnits;

    if (this.input?.template?.type == AdvancedDatasetInputType.RelativeDates || this.input?.template?.type == AdvancedDatasetInputType.RelativeYearPeriod) {
      units = this.relativeDatesLimitUnitTypes(this.input.template.id.startsWith('DateRange/D')
        ? DatesetEditorDateTimeUnitsWithDays : DatesetEditorDateTimeUnits
      );
    }

    return units.filter((unit)=>!(unit.exclusiveToProducts?.length && !unit.exclusiveToProducts.includes(this.product)))
  }

  get showSingularTitles() {
    return this.relativeDatesOperator == AdvancedDatasetRelativeDatesOperator.InThis;
  }

  get showUnitCount() {
    return this.relativeDatesOperator != AdvancedDatasetRelativeDatesOperator.InThis;
  }

  get showIncludeToday() {
    return this.relativeDatesOperator != AdvancedDatasetRelativeDatesOperator.InThis
      && this.relativeDatesOperator != AdvancedDatasetRelativeDatesOperator.Range
      && (!isCalendarUnit(this.relativeDatesValue.unitType) || this.isSpendUnit);
  }

  get isSpendUnit() {
    return this.relativeDatesValue.unitType == RelativeDateTimeFilterUnit.SpendPeriod
  }

  get isRange() {
    return this.relativeDatesOperator == AdvancedDatasetRelativeDatesOperator.Range;
  }

  relativeDatesUpdateInLastMaximum() {
    if (this.maximumDateRange < 0)
      return;

    this.relativeMaxUnits = maximumUnitsFromUnitType(this.relativeDatesValue.unitType, this.maximumDateRange);
  }

  relativeDatesUpdateRangeMaximum() {
    if (this.maximumDateRange < 0)
      return;

    //since the other value is required we make sure there is atleast 1 unit free for it
    const monthsUsed = unitsAsMonths(this.relativeDatesValue.unitType, Math.max(1,this.relativeDatesValue.unitsCount));

    const maxMonths = Math.floor(this.maximumDateRange - monthsUsed);
    this.relativeMaxUnits2 = maximumUnitsFromUnitType(this.relativeDatesValue.unit2Type, maxMonths);


    const monthsUsed2 = unitsAsMonths(this.relativeDatesValue.unit2Type, Math.max(1, this.relativeDatesValue.units2Count));

    const maxMonths2 = Math.floor(this.maximumDateRange - monthsUsed2);
    this.relativeMaxUnits = maximumUnitsFromUnitType(this.relativeDatesValue.unitType, maxMonths2)

    //Ensure invalid values are marked as dirty for when a value becomes invalid due to type changes
    if (this.relativeMaxUnits < this.relativeDatesValue.unitsCount || this.relativeMaxUnits2 < this.relativeDatesValue.units2Count) {
      const inputClass = `dataset-editor-input-${this.input.id}`

      const elements = document.getElementsByClassName(inputClass)
      for (const element of Array.from(elements)) {
        element.classList.remove('ng-pristine')
      }
    }
  }
  //the const relativeDateUnits pulls from doesnt have type
  relativeDatesLimitUnitTypes(unitTypes: DateTimeUnitInfo[]) {
    if (this.maximumDateRange <= 0)
      return unitTypes;

    return unitTypes.filter(t => maximumUnitsFromUnitType(t.id, this.maximumDateRange) > 0);
  }

  relativeDatesStartDateMonthSelected(date: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    if (this.input.template.type == AdvancedDatasetInputType.RelativeYearPeriod) {
      datepicker.close();
      this.startDate = date;
      this.onStartDateChange(this.startDate);
    }
  }

  relativeDatesEndDateMonthSelected(date: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    if (this.input.template.type == AdvancedDatasetInputType.RelativeYearPeriod) {
      datepicker.close();
      this.endDate = date;
      this.onEndDateChange(this.endDate);
    }
  }
  //#endregion

  //#region Dropdown
  dropdownValue: DropdownOptionDto | DropdownOptionDto[];

  get singleDropdownValue(): DropdownOptionDto { return <DropdownOptionDto>this.dropdownValue; }
  get dropdownValueArray(): DropdownOptionDto[] { return <DropdownOptionDto[]>this.dropdownValue; }

  dropdownLabel = '';

  dropdownTemplate: AdvancedDatasetDropdownInputDto;
  searchable = false;
  minSearchTermLength = 0;
  searchTerm = '';

  loadingDropdownOptions = false;
  dropdownOptionsLoaded = false;
  dropdownOptions: DropdownOptionDto[] = [];
  dropdownColumns: string[] = null;
  dropdownColumnsGridStyle = '';

  dropdownSearchControl = new FormControl('');

  defaultOption: DropdownOptionDto = null;
  defaultIsEmpty = false;

  searchTermNotLongEnough = false;

  @ViewChild('dropdownMatSelect') dropdownMatSelect: MatSelect;
  //#endregion

  //#region YearPeriod
  yearPeriodDate: moment.Moment;

  onYearPeriodChange(value: moment.Moment) {
    this.onValueChange();
  }

  yearPeriodSelected(date: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    if (this.input.template.type == AdvancedDatasetInputType.YearPeriod) {
      datepicker.close();
      this.yearPeriodDate = date;
      this.onYearPeriodChange(this.yearPeriodDate);
    }
  }
  //#endregion

  constructor(
    public readonly dropdownService: CustomDatasetDropdownService,
    public readonly element: ElementRef<HTMLElement>,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly ngZone: NgZone,
    private readonly datasetEditorDialog: DatasetEditorDialogComponent) {
  }

  ngAfterViewInit() { }

  ngOnInit() {
    this.dropdownSearchControl.valueChanges.pipe(
      map(s => ('' + s).trim()),
      tap(() => this.dropdownOptionsFilter = null),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(s => this.loadDropdownOptions(s))
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.product)
      this.relativeDateUnits = this.getRelativeDateUnits();

    if (changes.input) {
      if (this.input) {
        switch (this.input.template.type) {
          case AdvancedDatasetInputType.Checkbox: {
            const checkboxTemplate = <AdvancedDatasetCheckboxInputDto>this.input.template;

            this.checkboxValue = (this.input.value == checkboxTemplate.trueValue);
          } break;

          case AdvancedDatasetInputType.Text:
            this.textValue = this.input.value;
            break;

          case AdvancedDatasetInputType.WholeNumber:
          case AdvancedDatasetInputType.Number:
          case AdvancedDatasetInputType.Currency: {
            this.textValue = this.input.value;
          } break;

          case AdvancedDatasetInputType.CurrencyRange: {
            const split = this.input.value?.split(',');

            this.startNumber = this.parseNumberValue(split[0]);
            this.endNumber = this.parseNumberValue(split[1]);
          } break;

          case AdvancedDatasetInputType.Date: {
            this.startDate = this.parseDateValue(this.input.value);
          } break;

          case AdvancedDatasetInputType.DateRange: {
            const split = this.input.value?.split(',');

            this.startDate = this.parseDateValue(split[0]);
            this.endDate = this.parseDateValue(split[1]);
          } break;

          case AdvancedDatasetInputType.Dropdown: {
            this.dropdownTemplate = <AdvancedDatasetDropdownInputDto>this.input.template;
            const autoDefault = this.dropdownTemplate.autoSelectOnlyOption && this.dropdownTemplate.details.required && isNullOrUndefined(this.dropdownTemplate.details.defaultValue)

            if (!autoDefault) {
              this.setDefaultDropdownValue();
            }

            this.searchable = this.dropdownTemplate.searchable;
            this.minSearchTermLength = this.dropdownTemplate.minSearchTermLength;

            if (this.dropdownTemplate.multiple) {
              this.dropdownValue = getDropdownListItems(this.input.value) ?? [];
            }
            else {
              this.dropdownValue = getDropdownListItem(this.input.value);
            }

            this.setDropdownLabel();
            this.setDropdownPlaceholder();

            this.dropdownSearchControl.reset('', { emitEvent: false });

            if (autoDefault) {
              this.dropdownAutoDefaultOption();
            }
          } break;

          case AdvancedDatasetInputType.RelativeDates:
          case AdvancedDatasetInputType.RelativeYearPeriod: {
            this.maximumDateRange = this.input.template.details.maximumDateRange;
            this.relativeDatesValue = {
              operator: AdvancedDatasetRelativeDatesOperator.SpecificDates,
              ...this.input.value
            };

            this.relativeDatesUpdateInLastMaximum();
            this.relativeDatesUpdateRangeMaximum();

            this.relativeDatesOperator = this.relativeDatesValue.operator ?? AdvancedDatasetRelativeDatesOperator.InLast;
            this.relativeDates = this.relativeDatesOperator != AdvancedDatasetRelativeDatesOperator.SpecificDates;
            this.startDate = this.relativeDatesValue.startDate;
            this.endDate = this.relativeDatesValue.endDate;

            this.relativeDateUnits = this.getRelativeDateUnits();

            this.filterValuesSub.next(this.getCompleteRelativeFilterValues(this.relativeDatesValue));
          } break;

          case AdvancedDatasetInputType.YearPeriod: {
            const yearPeriod = <YearPeriod>this.input.value;

            if (yearPeriod) {
              const now = moment.utc('2000-01-01T00:00:00Z');
              now.year(yearPeriod.year);
              now.month(yearPeriod.period - 1);
              this.yearPeriodDate = now;
            }
            else {
              this.yearPeriodDate = null;
            }
          } break;

          case AdvancedDatasetInputType.NumberRange: {
            const value = <NumberRangeValue>this.input.value;
            const template = <AdvancedDatasetNumberRangeInputDto>this.input.template;

            this.numberRangeValueMin = template.min;
            this.numberRangeValueMax = template.max;

            this.numberRangeValue1 = value?.min;
            this.numberRangeValue2 = value?.max;
          } break;
        }
      }
    }
  }

  dropdownOpenedChange(isOpen: boolean) {
    if (!isOpen)
      return;

    if (this.loadingDropdownOptions)
      return;

    if (this.dropdownOptionsLoaded)
      return;

    if (!this.searchable || this.isSearchTermSufficient())
      this.loadDropdownOptions(this.searchTerm).subscribe();
    else {
      this.mergeSelectionIntoDropdownOptions();

      if (this.dropdownOptions.length > 0) {
        this.setDropdownColumns();
        this.dropdownOptionsLoaded = true;
      }
    }
  }

  private isSearchTermSufficient() {
    return !this.minSearchTermLength || (this.searchTerm && this.searchTerm.length >= this.minSearchTermLength);
  }

  dropdownOptionsFilter: RegExp;
  dropdownPlaceholder = '';

  private loadDropdownOptions(filter: string, ignoreSearchTerm = false) {
    try {
      this.searchTerm = filter;

      this.dropdownOptionsLoaded = false;
      this.dropdownOptions = [];
      this.dropdownColumns = null;
      this.dropdownOptionsFilter = null;

      if (!ignoreSearchTerm) {
        if (this.searchable && !this.isSearchTermSufficient()) {
          this.searchTermNotLongEnough = true;
          return of(null);
        }
      }

      this.searchTermNotLongEnough = false;

      this.dropdownOptionsFilter = filter ? createSearchPattern(filter) : null;

      this.loadingDropdownOptions = true;

      this.changeDetectorRef.markForCheck();

      const inputs = this.datasetEditorDialog.getCurrentInputMap();
      const parameters: Record<string, AdvancedDatasetInputDto> = {};

      for (const i of inputs.values())
        parameters[i.id] = i;

      return this.dropdownService.listDropdownOptions({
        product: this.product,
        datasetId: this.dataset.id,
        dropdownId: this.input.template.id,
        searchTerm: filter,
        parameters: parameters
      }).pipe(
        map(options => {
          this.dropdownOptions = options.sort((a,b) => a.order - b.order);

          this.setDropdownColumns();
        }),
        catchAppError(err => of(err)),
        tap(() => {
          this.mergeSelectionIntoDropdownOptions();

          this.loadingDropdownOptions = false;

          this.dropdownOptionsLoaded = true;

          this.changeDetectorRef.markForCheck();
        })
      );
    } finally {
      this.changeDetectorRef.markForCheck();
    }
  }

  private dropdownAutoDefaultOption() {
    this.dropdownLabel = "Loading...";
    this.defaultIsEmpty = true;
    this.changeDetectorRef.markForCheck();

    this.loadDropdownOptions('', true).subscribe(() => {
      const optionsWithoutNulls = this.dropdownOptions.filter(o => o.value);

      if (optionsWithoutNulls.length != 1) {
        this.setDropdownLabel();

        this.changeDetectorRef.markForCheck()
        return;
      }


      this.dropdownValue = optionsWithoutNulls[0];

      this.onValueChange();
      this.changeDetectorRef.markForCheck();
    })
  }

  private mergeSelectionIntoDropdownOptions() {
    let mergedOptions: DropdownOptionDto[] = [];

    if (this.dropdownTemplate.multiple) {
      mergedOptions = [...this.dropdownValueArray];
    }
    else {
      mergedOptions = [this.singleDropdownValue];
    }

    if (this.defaultIsEmpty)
      mergedOptions = mergedOptions.filter(x => !this.isDropdownDefault(x));

    const values = new Map<any, number>(mergedOptions.map((o, i) => [o.value, i]));

    for (const opt of this.dropdownOptions) {
      const index = values.get(opt.value);
      if (isNullOrUndefined(index)) {
        mergedOptions.push(opt);
      }
      else {
        mergedOptions[index] = {
          ...{ label: '', properties: [] },
          ...mergedOptions[index],
          // Merge with loaded option, so the label and properties are updated as necessary
          ...opt
        };
      }
    }

    this.dropdownOptions = mergedOptions;
  }


  private setDropdownLabel() {
    if (this.isDropdownDefaultSelected())
      this.dropdownLabel = this.defaultOption?.label ?? '';
    else {
      if (this.dropdownTemplate.multiple) {
        this.dropdownLabel = this.dropdownValueArray?.map(v => v.label).join(', ') ?? '';
      }
      else {
        this.dropdownLabel = this.singleDropdownValue?.label ?? '';
      }
    }
  }

  private setDropdownPlaceholder() {
    this.dropdownPlaceholder = this.dropdownLabel;
  }

  dropdownCompareWith(a: DropdownOptionDto, b: DropdownOptionDto) {
    return a?.value == b?.value;
  }

  private setDropdownColumns() {
    const firstOption = this.dropdownOptions?.length ? this.dropdownOptions[0] : null;

    if (!firstOption || !firstOption.properties || firstOption.properties.length < 2) {
      this.dropdownColumns = null;
      return;
    }

    this.dropdownColumns = firstOption.properties.map(p => p.key);
    this.dropdownColumnsGridStyle = `grid-template-columns: repeat(${this.dropdownColumns.length}, 1fr);`;
  }

  dropdownTrackBy(index: number, value: DropdownOptionDto) {
    return value.value;
  }


  onCheckboxChange(value: boolean) {
    this.onValueChange();
  }

  onTextChange(value: string) {
    this.onValueChange();
  }

  onDropdownChange(value: any) {
    this.onValueChange();
  }

  onStartDateChange(value: Date) {
    if (this.maximumDateRange > 0) {
      this.maxDate = value ? moment(value).add(this.maximumDateRange, 'months').subtract(1, 'days') : null;
    }

    this.onValueChange();
  }

  onEndDateChange(value: Date) {
    if (this.maximumDateRange > 0) {
      this.minDate =  value ? moment(value).subtract(this.maximumDateRange, 'months').add(1, 'days') : null;
    }

    this.onValueChange();
  }

  onValueChange() {
    switch (this.input.template.type) {
      case AdvancedDatasetInputType.Checkbox: {
        const checkboxTemplate = <AdvancedDatasetCheckboxInputDto>this.input.template;

        this.inputChange.emit({ value: this.checkboxValue ? checkboxTemplate.trueValue : checkboxTemplate.falseValue });
      } break;

      case AdvancedDatasetInputType.Text: {
        this.inputChange.emit({ value: this.textValue });
      } break;

      case AdvancedDatasetInputType.WholeNumber:
      case AdvancedDatasetInputType.Number:
      case AdvancedDatasetInputType.Currency: {
        this.inputChange.emit({ value: this.textValue });
      } break;

      case AdvancedDatasetInputType.CurrencyRange: {
        const value = `${this.stringifyNumberValue(this.startNumber)},${this.stringifyNumberValue(this.endNumber)}`;
        this.inputChange.emit({ value });
      } break;

      case AdvancedDatasetInputType.Date: {
        this.inputChange.emit({ value: this.stringifyDateValue(this.startDate) });
      } break;

      case AdvancedDatasetInputType.DateRange: {
        const value = `${this.stringifyDateValue(this.startDate)},${this.stringifyDateValue(this.endDate)}`;
        this.inputChange.emit({ value: value });
      } break;

      case AdvancedDatasetInputType.Dropdown: {
        if (this.dropdownTemplate.multiple) {
          let selectedOptions = this.dropdownValueArray;

          if (isNullOrUndefined(selectedOptions) || !Array.isArray(selectedOptions))
            selectedOptions = [];

          // Remove 'default' value if its present in the array
          selectedOptions = selectedOptions.filter(x => !this.dropdownCompareWith(x, this.defaultOption));

          this.dropdownValue = selectedOptions;
        }

        this.setDropdownLabel();
        this.setDropdownPlaceholder();

        this.inputChange.emit({
          value: this.dropdownValue,
          label: this.dropdownLabel
        });

        const inputId = this.input.id;
        const inputClass = `dataset-editor-input-${inputId}`;

        const elements = document.getElementsByClassName(inputClass);

        for (const element of Array.from(elements)) {
          element.classList.remove('ng-invalid');
        }

      } break;

      case AdvancedDatasetInputType.RelativeDates:
      case AdvancedDatasetInputType.RelativeYearPeriod: {
        const value = {
          ...this.relativeDatesValue
        };

        if (this.relativeDates) {
          if (this.relativeDatesOperator == AdvancedDatasetRelativeDatesOperator.SpecificDates)
            this.relativeDatesOperator = AdvancedDatasetRelativeDatesOperator.InLast;

          value.operator = this.relativeDatesOperator;

          if (this.relativeDatesOperator == AdvancedDatasetRelativeDatesOperator.Range) {
            this.relativeDatesUpdateRangeMaximum();
          } else {
            this.relativeDatesUpdateInLastMaximum();
          }
        }
        else {
          value.operator = AdvancedDatasetRelativeDatesOperator.SpecificDates;
          value.startDate = this.stringifyDateValue(this.parseDateValue(this.startDate));
          value.endDate = this.stringifyDateValue(this.parseDateValue(this.endDate));
        }

        let emitValue = value;

        if (this.isAdvancedDatasetRelativeDatesInputValueDto(value)) {
            const inputChanges = value as AdvancedDatasetRelativeDatesInputValueDto;
            const filterValues = this.getCompleteRelativeFilterValues(inputChanges);

            this.filterValuesSub.next(filterValues);

            // If Relative Dates is selected but not fully configured (e.g. no count entered
            // yet), treat it the same as no date range at all instead of saving/sending an
            // incomplete value that would otherwise resolve to a narrow, arbitrary range.
            if (this.relativeDates && !filterValues)
              emitValue = null;
        }

        this.inputChange.emit({ value: emitValue });
      } break;

      case AdvancedDatasetInputType.YearPeriod: {
        const value: YearPeriod = this.yearPeriodDate?.isValid() ? {
          year: this.yearPeriodDate.year(),
          period: this.yearPeriodDate.month() + 1
        } : null;

        this.inputChange.emit({ value });
      } break;

      case AdvancedDatasetInputType.NumberRange: {
        const { value1, value2 } = this.getNumberRange(this.numberRangeValue1, this.numberRangeValue2);

        this.validateNumberRange();

        const value: NumberRangeValue = {
          min: value1,
          max: value2,
        };

        this.inputChange.emit({ value });
      } break;
    }
  }

  private isAdvancedDatasetRelativeDatesInputValueDto(changes: any): changes is AdvancedDatasetRelativeDatesInputValueDto {
    return (
      changes && typeof changes.operator !== 'undefined'
    );
  }

  private mapFromAdvancedDatasetToRelativeFilterDto(inputChanges: AdvancedDatasetRelativeDatesInputValueDto): RelativeDateOrTimeValuesDto {
    return {
      includeToday: inputChanges.includeToday,
      operator: this.mapAdvancedDatasetOperatorToRelativeDateOperator(inputChanges.operator),
      unitType: inputChanges.unitType,
      unit2Type: inputChanges.unit2Type,
      unitsCount: inputChanges.unitsCount,
      units2Count: inputChanges.units2Count,
    }
  }

  // Returns the mapped filter values only if enough fields are filled in to compute a real
  // date range; otherwise null, so the preview/backend treat it the same as no filter at all.
  private getCompleteRelativeFilterValues(inputChanges: AdvancedDatasetRelativeDatesInputValueDto): RelativeDateOrTimeValuesDto {
    if (!this.isAdvancedDatasetRelativeDatesInputValueDto(inputChanges))
      return null;

    const filterValues = this.mapFromAdvancedDatasetToRelativeFilterDto(inputChanges);

    const isInRange = filterValues.operator === RelativeDateOrTimeOperator.InRange;
    const isInThis = filterValues.operator === RelativeDateOrTimeOperator.InThis;

    const isComplete = isInRange
      ? !!(filterValues.unitsCount && filterValues.unitType != null && filterValues.units2Count && filterValues.unit2Type != null)
      : isInThis
        ? filterValues.unitType != null
        : (filterValues.unitType != null && filterValues.unitsCount != null);

    return isComplete ? filterValues : null;
  }

  private mapAdvancedDatasetOperatorToRelativeDateOperator(operator: AdvancedDatasetRelativeDatesOperator): RelativeDateOrTimeOperator {
    switch (operator) {
      case AdvancedDatasetRelativeDatesOperator.InNext:
        return RelativeDateOrTimeOperator.InNext;
      case AdvancedDatasetRelativeDatesOperator.InLast:
        return RelativeDateOrTimeOperator.InLast;
      case AdvancedDatasetRelativeDatesOperator.InThis:
        return RelativeDateOrTimeOperator.InThis;
      case AdvancedDatasetRelativeDatesOperator.Range:
        return RelativeDateOrTimeOperator.InRange;
    }
  }


  private parseNumberValue(value: string) {
    if (isNullOrUndefined(value))
      return null;

    const n = Number(value);

    if (isNaN(n))
      return null;

    return n;
  }

  private stringifyNumberValue(n: number) {
    if (isNullOrUndefined(n))
      return '';

    return '' + n;
  }


  private parseDateValue(value: string | moment.Moment): moment.Moment {
    if (isNullOrUndefined(value))
      return null;

    if (moment.isMoment(value)){
      return value;
    }

    const m = moment.utc(value);

    if (!m.isValid())
      return null;

    return m;
  }

  private stringifyDateValue(date: moment.Moment) {
    if (!date) {
      return '';
    }
    return date.clone().utc(true).toISOString(false);
  }

  getViewportHeight() {
    let lines = 0;

    if (this.loadingDropdownOptions)
      lines = 3;
    else {
      if (this.dropdownOptionsLoaded) {
        lines = Math.min((this.dropdownOptions?.length || 1), 5);
      }
    }

    return lines * 24;
  }


  canShowClearValueButton() {
    return !(this.disabled || this.readonly || this.loadingDropdownOptions);
  }


  showClearDropdownValue() {
    if (this.isDropdownDefaultSelected())
      return false;

    return this.canShowClearValueButton();
  }

  clearDropdownValue() {
    if (this.isDropdownDefaultSelected())
      return;

    if (this.defaultOption)
      this.dropdownValue = this.dropdownTemplate.multiple ? [this.defaultOption] : this.defaultOption;
    else
      this.dropdownValue = this.dropdownTemplate.multiple ? [] :this.defaultOption;

    this.setDropdownLabel();
    this.setDropdownPlaceholder();
    this.dropdownOptions = [];
    this.dropdownColumns = [];
    this.dropdownOptionsLoaded = false;

    this.onValueChange();
  }

  isDropdownDefaultSelected() {
    if (this.dropdownTemplate.multiple) {
      if (this.defaultIsEmpty) {
        return this.dropdownValueArray.length == 0;
      }

      return this.dropdownValueArray.length == 1
        && this.isDropdownDefault(this.dropdownValueArray[0]);
    }

    return this.isDropdownDefault(this.singleDropdownValue);
  }

  isDropdownDefault(dropdownValue: DropdownOptionDto) {
    const value = this.unwrapDropdownValue(dropdownValue).value;

    if (this.defaultIsEmpty) {
      if (isNullOrUndefined(value))
        return true;
    }

    return value == this.defaultOption?.value;
  }

  showDropdownCount() {
    if (!this.dropdownValue || !Array.isArray(this.dropdownValue) || this.dropdownValue.length < 2)
      return false;

    return true;
  }

  setDefaultDropdownValue() {
    let defaultOption = this.dropdownTemplate.details.defaultValue;

    if (!isNullOrUndefined(defaultOption)) {
      let value = null;
      let label = null;

      if (typeof defaultOption == 'object') {
        const defaultValueOption = defaultOption;

        if ('value' in defaultValueOption) {
          value = defaultValueOption.value;
          label = defaultValueOption.label;
        }
        else {
          value = defaultValueOption.Value;
          label = defaultValueOption.Label;
        }
      }
      else {
        value = this.dropdownTemplate.details.defaultValue;
        label = value ? ('' + value) : '';
      }

      defaultOption = { value, label };
    }

    this.defaultOption = defaultOption;
    this.defaultIsEmpty = isNullOrUndefined(defaultOption?.value);
  }

  unwrapDropdownValue(dropdownValue: any): DropdownOptionDto {
    if (isNullOrUndefined(dropdownValue)) {
      return { value: null, label: null };
    }
    else {
      let value = null;
      let label = null;

      if (typeof dropdownValue == 'object') {
        const defaultValueOption = dropdownValue;

        if ('value' in defaultValueOption) {
          value = defaultValueOption.value;
          label = defaultValueOption.label;
        }
        else {
          value = defaultValueOption.Value;
          label = defaultValueOption.Label;
        }
      }
      else {
        value = this.dropdownTemplate.details.defaultValue;
        label = value ? ('' + value) : '';
      }

      dropdownValue = { value, label };
    }

    return dropdownValue;
  }


  showStartDateClearValue() {
    return !isNullOrUndefined(this.startDate) && moment(this.startDate).isValid() && this.canShowClearValueButton();
  }

  clearStartDateValue() {
    if (isNullOrUndefined(this.startDate))
      return;

    this.startDate = null;
    this.maxDate = null;

    this.onValueChange();
  }


  showEndDateClearValue() {
    return !isNullOrUndefined(this.endDate) && moment(this.endDate).isValid() && this.canShowClearValueButton();
  }

  clearEndDateValue() {
    if (isNullOrUndefined(this.endDate))
      return;

    this.endDate = null;
    this.minDate = null;

    this.onValueChange();
  }


  showYearPeriodClearValue() {
    return !isNullOrUndefined(this.yearPeriodDate) && this.yearPeriodDate.isValid() && this.canShowClearValueButton();
  }

  clearYearPeriodValue() {
    if (isNullOrUndefined(this.yearPeriodDate))
      return;

    this.yearPeriodDate = null;

    this.onValueChange();
  }


  getNumberRange(value1: number, value2: number) {
    if (!isNullOrUndefined(this.numberRangeValueMin)) {
      if (!isNullOrUndefined(value1))
        value1 = Math.max(value1, this.numberRangeValueMin);

      if (!isNullOrUndefined(value2))
        value2 = Math.max(value2, this.numberRangeValueMin);
    }
    if (!isNullOrUndefined(this.numberRangeValueMax)) {
      if (!isNullOrUndefined(value1))
        value1 = Math.min(value1, this.numberRangeValueMax);

      if (!isNullOrUndefined(value2))
        value2 = Math.min(value2, this.numberRangeValueMax);
    }

    if (!isNullOrUndefined(value1) && !isNullOrUndefined(value2)) {
      if (value1 > value2)
        value1 = value2;
      else if (value2 < value1)
        value2 = value1;
    }

    return { value1, value2 };
  }

  validateNumberRange() {
    setTimeout(() => {
      this.ngZone.run(() => {
        const newValues = this.getNumberRange(this.numberRangeValue1, this.numberRangeValue2);

        if (newValues.value1 != this.numberRangeValue1 || newValues.value2 != this.numberRangeValue2) {
          this.numberRangeValue1 = newValues.value1;
          this.numberRangeValue2 = newValues.value2;

          this.changeDetectorRef.detectChanges();
        }
      });
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}

function getDropdownListItems(val: any): DropdownOptionDto[] {
  if (Array.isArray(val)) {
    if (val.length == 0)
      return [];

    const result: DropdownOptionDto[] = [];

    for (let i = 0; i < val.length; ++i) {
      result.push(getDropdownListItem(val[i]));
    }

    return result;
  }

  if (!isNullOrUndefined(val))
    return [getDropdownListItem(val)];

  return [];
}

function getDropdownListItem(val: any): DropdownOptionDto {
  if (!isNullOrUndefined(val) && typeof val === 'object') {
    if ('Value' in val) {
      // INFO: The response from backend can be uppercase due to json formatting mismatch
      val = {
        value: val['Value'],
        label: val['Label'],
        properties: mapProperties(val['properties']),
      };
    }

    if ('value' in val) {
      return {
        // For the sake of compatibility, unwrap the underlying value into label/properties (likely just defaults)
        // Then override with any defined on the value itself
        // If the structure is { value: ... }, we end up with { value: ..., label: '...', properties: [] }
        ...getDropdownListItem(val.value),
        ...val,
        properties: mapProperties(val['properties'])
      };
    }
  }

  return {
    value: val,
    label: isNullOrUndefined(val) ? '' : ('' + val),
    properties: []
  };

  function mapProperties(properties: any) {
    if (!properties || !Array.isArray(properties))
      return properties;

    return properties.map(p => (('value' in p) ? p : {
      key: p['Key'],
      value: p['Value'],
    }));
  }
}

export function maximumUnitsFromUnitType(unitType: RelativeDateTimeFilterUnit, maximumMonths: number) {
  if (maximumMonths <= 0)
    return 0;

  return Math.floor(maximumMonths / unitsAsMonths(unitType, 1))
}

export function unitsAsMonths(unitType: RelativeDateTimeFilterUnit, units: number) {
  let months: number;

  if (unitType == RelativeDateTimeFilterUnit.CalendarHalfYears) {
    months = 6
  }else {
    const type = relativeDateTimeUnitToMomentUnit.get(unitType)
    months = moment.duration(1, type).asMonths()
  }

  return months * units
}
